/**
 * Security utilities for the application
 * Handles CSP, input sanitization, XSS prevention, and security headers
 */

import { encryptionService } from './encryption';
import DOMPurify from 'dompurify';

export interface SecurityConfig {
  enableCSP: boolean;
  enableHSTS: boolean;
  enableXSSProtection: boolean;
  enableContentTypeOptions: boolean;
  enableFrameOptions: boolean;
  maxRequestSize: number;
  rateLimitWindow: number;
  maxRequestsPerWindow: number;
}

export class SecurityManager {
  private static instance: SecurityManager;
  private config: SecurityConfig;
  private requestCounts: Map<string, { count: number; resetTime: number }> =
    new Map();

  private constructor() {
    this.config = {
      enableCSP: true,
      enableHSTS: true,
      enableXSSProtection: true,
      enableContentTypeOptions: true,
      enableFrameOptions: true,
      maxRequestSize: 10 * 1024 * 1024, // 10MB
      rateLimitWindow: 60000, // 1 minute
      maxRequestsPerWindow: 100
    };
  }

  public static getInstance(): SecurityManager {
    if (!SecurityManager.instance) {
      SecurityManager.instance = new SecurityManager();
    }
    return SecurityManager.instance;
  }

  /**
   * Initialize security headers and CSP
   */
  public initializeSecurity(): void {
    if (this.config.enableCSP) {
      this.setCSP();
    }

    if (this.config.enableHSTS) {
      this.setHSTS();
    }
  }

  /**
   * Set Content Security Policy
   * Note: For optimal security, CSP should be set via HTTP headers on the server.
   * This implementation serves as a client-side fallback.
   */
  private setCSP(): void {
    // Allow all for localhost and 192.168.0.84 (dev)
    const hostname = window.location.hostname;
    let csp: string;
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '192.168.0.84'
    ) {
      // Permissive CSP for local dev
      csp =
        "default-src *; script-src * 'unsafe-inline' 'unsafe-eval'; style-src * 'unsafe-inline'; img-src * data: blob:; connect-src *; media-src *; font-src *; object-src *;";
    } else {
      // Strict CSP for production
      csp = [
        "default-src 'self'",
        "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://cdn.jsdelivr.net https://unpkg.com",
        "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
        "font-src 'self' https://fonts.gstatic.com",
        "img-src 'self' data: blob: https:",
        "connect-src 'self' https: wss:",
        "media-src 'self' https:",
        "object-src 'none'",
        "base-uri 'self'",
        "form-action 'self'",
        // frame-ancestors is ignored in <meta> tags and causes warnings
        'upgrade-insecure-requests'
      ].join('; ');
    }

    // Set CSP meta tag
    let cspMeta = document.querySelector(
      'meta[http-equiv="Content-Security-Policy"]'
    );
    if (!cspMeta) {
      cspMeta = document.createElement('meta');
      cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
      document.head.appendChild(cspMeta);
    }
    cspMeta.setAttribute('content', csp);
  }

  /**
   * Set HTTP Strict Transport Security
   */
  private setHSTS(): void {
    // This would typically be set on the server side
    // For client-side, we can add a meta tag as fallback
    let hstsMeta = document.querySelector(
      'meta[http-equiv="Strict-Transport-Security"]'
    );
    if (!hstsMeta) {
      hstsMeta = document.createElement('meta');
      hstsMeta.setAttribute('http-equiv', 'Strict-Transport-Security');
      hstsMeta.setAttribute('content', 'max-age=31536000; includeSubDomains');
      document.head.appendChild(hstsMeta);
    }
  }

  /**
   * Sanitize HTML content to prevent XSS
   */
  public sanitizeHTML(html: string): string {
    const div = document.createElement('div');
    div.textContent = html;
    return div.innerHTML;
  }

  /**
   * Sanitize user input
   */
  public sanitizeInput(input: string): string {
    return input
      .replace(/[<>]/g, '') // Remove < and >
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  }

  /**
   * Validate and sanitize URL
   */
  public sanitizeURL(url: string): string | null {
    try {
      const parsed = new URL(url);
      // Only allow http, https, and relative URLs
      if (
        !['http:', 'https:'].includes(parsed.protocol) &&
        !parsed.protocol.startsWith('//')
      ) {
        return null;
      }
      return parsed.toString();
    } catch {
      return null;
    }
  }

  /**
   * Rate limiting for API requests
   * NOTE: This is a client-side check for UX purposes (preventing accidental double-clicks).
   * It does NOT replace server-side rate limiting.
   */
  public checkRateLimit(identifier: string): boolean {
    const now = Date.now();
    const record = this.requestCounts.get(identifier);

    if (!record || now > record.resetTime) {
      this.requestCounts.set(identifier, {
        count: 1,
        resetTime: now + this.config.rateLimitWindow
      });
      return true;
    }

    if (record.count >= this.config.maxRequestsPerWindow) {
      return false;
    }

    record.count++;
    return true;
  }

  /**
   * Validate file upload security
   */
  public validateFileUpload(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > this.config.maxRequestSize) {
      return {
        valid: false,
        error: `File size exceeds maximum allowed size of ${this.config.maxRequestSize / (1024 * 1024)}MB`
      };
    }

    // Check file type
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'File type not allowed'
      };
    }

    // Check filename for path traversal
    if (
      file.name.includes('..') ||
      file.name.includes('/') ||
      file.name.includes('\\')
    ) {
      return {
        valid: false,
        error: 'Invalid filename'
      };
    }

    return { valid: true };
  }

  /**
   * Generate secure nonce for CSP
   */
  public generateNonce(): string {
    return encryptionService.generateSecureRandom(16);
  }

  /**
   * Validate CSRF token
   */
  public validateCSRFToken(token: string, expectedToken: string): boolean {
    return token === expectedToken;
  }

  /**
   * Generate CSRF token
   */
  public generateCSRFToken(): string {
    return encryptionService.generateSecureRandom(32);
  }

  /**
   * Secure cookie utilities
   */
  public setSecureCookie(
    name: string,
    value: string,
    options: {
      maxAge?: number;
      path?: string;
      domain?: string;
      secure?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
    } = {}
  ): void {
    const {
      maxAge = 86400, // 1 day
      path = '/',
      domain,
      secure = true,
      sameSite = 'Strict'
    } = options;

    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    cookie += `; Max-Age=${maxAge}`;
    cookie += `; Path=${path}`;

    if (domain) {
      cookie += `; Domain=${domain}`;
    }

    if (secure) {
      cookie += '; Secure';
    }

    cookie += `; SameSite=${sameSite}`;

    // Note: HttpOnly cookies cannot be set from client-side JavaScript.
    // They must be set by the server via Set-Cookie header.
    document.cookie = cookie;
  }

  public getSecureCookie(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === encodeURIComponent(name)) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  }

  /**
   * Clear rate limiting data
   */
  public clearRateLimitData(): void {
    this.requestCounts.clear();
  }

  /**
   * Get security configuration
   */
  public getConfig(): SecurityConfig {
    return { ...this.config };
  }

  /**
   * Update security configuration
   */
  public updateConfig(newConfig: Partial<SecurityConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

export const securityManager = SecurityManager.getInstance();

/**
 * Input validation utilities
 */
export class InputValidator {
  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  static isStrongPassword(password: string): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate phone number
   */
  static isValidPhone(phone: string): boolean {
    const phoneRegex = /^\+?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  }

  /**
   * Validate URL
   */
  static isValidURL(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate and sanitize JSON
   */
  static sanitizeJSON(jsonString: string): unknown {
    try {
      const parsed = JSON.parse(jsonString);
      return parsed;
    } catch {
      return null;
    }
  }
}

/**
 * XSS Prevention utilities
 */
export class XSSPrevention {
  /**
   * Escape HTML entities
   */
  static escapeHTML(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Unescape HTML entities
   */
  static unescapeHTML(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  }

  /**
   * Strip all HTML tags
   */
  static stripHTML(html: string): string {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
  }

  /**
   * Validate and clean HTML content
   * Uses DOMPurify for battle-tested sanitization
   */
  static cleanHTML(
    html: string,
    allowedTags: string[] = ['p', 'br', 'strong', 'em', 'u']
  ): string {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: ['href', 'target', 'rel'] // Basic safe attributes
    });
  }
}
